import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { GET_LATEST_CHECK_RUN_STATUS } from "../../../apis/common-apis/commonApis";
import { CHECK_RUN_STATUS } from "../../../utils/enums";

const POLLING_INTERVAL = 10000;
const MAX_ATTEMPTS = 90;

const isTerminalStatus = (status) =>
  status == null ||
  status === CHECK_RUN_STATUS.COMPLETED ||
  status === CHECK_RUN_STATUS.FAILED;
const isRunningStatus = (status) => status === CHECK_RUN_STATUS.RUNNING;

export const useCheckRunStatusPolling = (activeCheckId, onComplete) => {
  const [isPollingRunStatus, setIsPollingRunStatus] = useState(false);

  const onCompleteRef = useRef(onComplete);
  const pollCountRef = useRef(0);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const { refetch } = useQuery(GET_LATEST_CHECK_RUN_STATUS, {
    variables: { check_id: activeCheckId },
    skip: true,
    fetchPolicy: "network-only",
  });

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPollingRunStatus(false);
    pollCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (!activeCheckId) return;

    const checkInitialStatus = async () => {
      try {
        const res = await refetch();
        const runStatus = res.data?.getLatestCheckRunStatus?.run_status;

        if (isRunningStatus(runStatus)) {
          setIsPollingRunStatus(true);
        } else {
          setIsPollingRunStatus(false);
        }
      } catch (err) {
        console.error("Failed to check initial status:", err);
      }
    };

    checkInitialStatus();
  }, [activeCheckId, refetch]);

  // Polling effect
  useEffect(() => {
    if (!isPollingRunStatus || !activeCheckId) {
      stopPolling();
      return;
    }

    const poll = async () => {
      if (document.hidden) return;

      try {
        const res = await refetch();
        const runStatus = res.data?.getLatestCheckRunStatus?.run_status;

        pollCountRef.current += 1;

        if (isTerminalStatus(runStatus) || pollCountRef.current >= MAX_ATTEMPTS) {
          stopPolling();
          onCompleteRef.current?.();
        }
      } catch (err) {
        console.error("Polling error:", err);
        pollCountRef.current += 1;

        if (pollCountRef.current >= MAX_ATTEMPTS) {
          stopPolling();
        }
      }
    };

    poll();
    pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isPollingRunStatus, activeCheckId, refetch, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // Already polling
    pollCountRef.current = 0;
    setIsPollingRunStatus(true);
  }, []);

  return { isPollingRunStatus, startPolling };
};
